import { IonRadioGroupCustomEvent, RadioGroupChangeEventDetail } from '@ionic/core';
import {
  IonAlert,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonTitle,
  IonToolbar,
  useIonRouter,
} from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import { arrowBackOutline, chevronBack, lockClosed } from 'ionicons/icons';
import RCField, { useForm } from 'rc-field-form';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { jt, t } from 'ttag';

import Content from '@components/Content';
import Field from '@components/Form/Field';
import InputField from '@components/Form/InputField';
import { PageTitle } from '@components/PageTitle';
import { SectionTitle } from '@components/SectionTitle';
import SessionItem from '@components/SessionItem';
import Platform from '@custom-types/Platform';
import RoutePath from '@custom-types/RoutePath';
import useLoading from '@hooks/useLoading';
import { usePrinter } from '@hooks/usePrinter';
import { ErrorCode } from '@pages/Check/Enums';
import { activateKiosk as activateKioskFn } from '@pages/Check/service';
import { generateRoute } from '@services/navigation-service';
import { getCheckOutEnabled, getEventInfo } from '@state/event';
import {
  getKioskMode,
  getKioskName,
  setKioskEnabled,
  setKioskMode,
  setKioskName,
} from '@state/kiosk';
import { clearSessionInfo, getSessionInfo } from '@state/session';
import { getIsOnline, getLocale } from '@state/system';
import { Capacitor } from '~capacitor-plugins/core';

import { KioskMode } from './service';

const Form = styled(RCField)`
  width: 100%;
  padding-top: 16px;
  display: flex;
  flex-direction: column;
`;

const RadioGroup = styled(IonRadioGroup)`
  margin-top: 16px;
`;

const Footer = styled(IonFooter)`
  padding: 16px 16px 48px;
`;

const SessionPanel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Item = styled(IonItem)`
  --border-style: none;
  margin-bottom: 0;
`;

const IosBackIcon = styled(IonIcon)`
  margin: 0 -5px 0 -10px;
`;

const getError = (code: ErrorCode) => {
  if (code === ErrorCode.KioskModeNotPermitted) {
    return {
      header: t`Service Error`,
      message: t`We are unable to process your request. The Kiosk service has been turned off. Please contact an event staff for assistance.`,
    };
  }
  return {
    header: t`Error`,
    message: t`We were unable to process your request at this time. Please try again`,
  };
};

const selector = createSelector(
  getIsOnline,
  getEventInfo,
  getSessionInfo,
  getKioskName,
  getKioskMode,
  getLocale,
  getCheckOutEnabled,
  (
    isOnline,
    { id: eventId, name: eventName },
    session,
    kioskName,
    kioskMode,
    locale,
    checkOutEnabled
  ) => ({
    isOnline,
    eventId,
    eventName,
    session,
    kioskName,
    kioskMode,
    locale,
    checkOutEnabled,
  })
);

const SelectSession = () => {
  const { eventId, session, locale } = useSelector(selector);
  const { push: pushRoute } = useIonRouter();

  return (
    <SessionPanel>
      {session && (
        <div style={{ flex: 1 }}>
          <SessionItem locale={locale} session={session} showBorder={false} />
        </div>
      )}
      <IonButton
        style={session ? {} : { flex: 1 }}
        expand="block"
        fill="outline"
        // skipcq: JS-0417
        onClick={() => {
          pushRoute(generateRoute(RoutePath.SessionList, { eventId, goBack: true }));
        }}
      >
        {session ? t`Select a New Session` : t`Select Session`}
      </IonButton>
    </SessionPanel>
  );
};

interface RadioProps {
  value: KioskMode;
  additionalComponent?: React.ReactNode;
  children: React.ReactNode;
}

const Radio = ({ value, children, additionalComponent = undefined }: RadioProps) => (
  <>
    <Item>
      <IonRadio mode="md" justify="start" labelPlacement="end" color="primary" value={value}>
        {children}
      </IonRadio>
    </Item>
    {additionalComponent && (
      <div
        style={{
          marginLeft: '32px',
        }}
      >
        {additionalComponent}
      </div>
    )}
    <br />
  </>
);

const isKioskInPrintMode = (mode: KioskMode) =>
  mode === KioskMode.BadgePrinting || mode === KioskMode.EventCheckInAndBadgePrinting;

const Setup = () => {
  const { push: pushRoute } = useIonRouter();
  const dispatch = useDispatch();
  const [form] = useForm();
  const { isOnline, eventId, eventName, session, kioskName, kioskMode, checkOutEnabled } =
    useSelector(selector);
  const { presentLoading, dismissLoading } = useLoading();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState({ header: '', message: '' });
  const { enabled: printingEnabled, needsSetup: needsPrinterSetup } = usePrinter();
  const platform = Capacitor.getPlatform();

  const { validateFields } = form;

  const onFinish = async () => {
    setSubmitting(true);
    try {
      const { name } = await validateFields();
      await presentLoading();
      await activateKioskFn(eventId);
      if (kioskMode !== KioskMode.SessionCheckIn && kioskMode !== KioskMode.SessionCheckOut) {
        dispatch(clearSessionInfo());
      }
      dispatch(setKioskEnabled(true));
      dispatch(setKioskName(name));
      dispatch(setKioskMode(kioskMode!));
      pushRoute(generateRoute(RoutePath.KioskCheck, { eventId }), 'root');

      // skipcq: JS-0323
    } catch (err: any) {
      // If errorFields is an non-empty array, it means there are errors in the form.
      // Then no need to setError modal.
      if (err?.errorFields?.length > 0) {
        return;
      }
      setError(getError(err?.errors?.[0]?.code));
    } finally {
      await dismissLoading();
      setSubmitting(false);
    }
  };

  const goBack = useCallback(
    () => pushRoute(generateRoute(RoutePath.EventDetail, { eventId })),
    [pushRoute, eventId]
  );

  const goToPrinterSettings = useCallback(
    () => pushRoute(generateRoute(RoutePath.PrinterSettings, { eventId })),
    [pushRoute, eventId]
  );

  const lockIcon = <IonIcon key="lockClosed" icon={lockClosed} />;
  const canLaunchKiosk =
    isOnline &&
    !submitting &&
    !!kioskMode &&
    (!!session || ![KioskMode.SessionCheckIn, KioskMode.SessionCheckOut].includes(kioskMode)) &&
    !(isKioskInPrintMode(kioskMode) && needsPrinterSetup);

  const changeKioskMode = (e: IonRadioGroupCustomEvent<RadioGroupChangeEventDetail<KioskMode>>) => {
    const mode: KioskMode = e.detail.value;
    dispatch(setKioskMode(mode));
    // If the kiosk mode is set to a printing mode, but there is no printer selected, navigate to
    // the printer settings screen
    if (isKioskInPrintMode(mode) && needsPrinterSetup) {
      goToPrinterSettings();
    }
  };

  // We can't use a typical <Layout> here because unlocking a kiosk and clicking the stock back
  // button would bring you back into the kiosk. Instead, we need to use a custom layout that
  // has a back button hardcoded to go back to the event details.
  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={goBack} routerDirection="back">
              {platform === Platform.Ios ? (
                <>
                  <IosBackIcon data-testid="ios-back-btn" icon={chevronBack} size="large" />
                  {t`Back`}
                </>
              ) : (
                <IonIcon data-testid="android-back-btn" icon={arrowBackOutline} size="large" />
              )}
            </IonButton>
          </IonButtons>
          <IonTitle className="ion-text-wrap">{eventName}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Content style={{ alignItems: 'self-start' }}>
          <PageTitle>{t`Set up Kiosk`}</PageTitle>
          <SectionTitle>{t`Settings`}</SectionTitle>
          <p>{t`Please enter a Kiosk Name. This will be used for Event Analytics.`}</p>
          <Form
            form={form}
            // skipcq: JS-0417
            onFinish={onFinish}
            name="kiosk-setup"
            id="kiosk-setup"
            initialValues={{ name: kioskName }}
          >
            <Field name="name" rules={[{ required: true, message: t`Name is required.` }]}>
              <InputField
                inputMode="text"
                role="textbox"
                aria-label="name"
                label={t`Kiosk Name`}
                labelPlacement="stacked"
                // skipcq: JS-0417
                onBlur={e => dispatch(setKioskName(e.target.value?.toString() ?? ''))}
              />
            </Field>
            <RadioGroup value={kioskMode} onIonChange={changeKioskMode}>
              <Radio value={KioskMode.EventCheckIn}>{t`Event Check In`}</Radio>

              {printingEnabled && (
                <Radio
                  value={KioskMode.EventCheckInAndBadgePrinting}
                  additionalComponent={
                    kioskMode === KioskMode.EventCheckInAndBadgePrinting && (
                      <IonButton
                        expand="block"
                        fill="outline"
                        role="button"
                        onClick={goToPrinterSettings}
                        color={needsPrinterSetup ? 'danger' : 'primary'}
                      >
                        {t`Printer Settings`}
                      </IonButton>
                    )
                  }
                >
                  {t`Event Check In & Badge Printing`}
                </Radio>
              )}

              {checkOutEnabled && (
                <Radio value={KioskMode.EventCheckOut}>{t`Event Check Out`}</Radio>
              )}

              <Radio
                value={KioskMode.SessionCheckIn}
                additionalComponent={kioskMode === KioskMode.SessionCheckIn && <SelectSession />}
              >
                {t`Session Check In`}
              </Radio>

              {checkOutEnabled && (
                <Radio
                  value={KioskMode.SessionCheckOut}
                  additionalComponent={kioskMode === KioskMode.SessionCheckOut && <SelectSession />}
                >
                  {t`Session Check Out`}
                </Radio>
              )}

              {printingEnabled && (
                <Radio
                  value={KioskMode.BadgePrinting}
                  additionalComponent={
                    kioskMode === KioskMode.BadgePrinting && (
                      <IonButton
                        expand="block"
                        fill="outline"
                        role="button"
                        onClick={goToPrinterSettings}
                        color={needsPrinterSetup ? 'danger' : 'primary'}
                      >
                        {t`Printer Settings`}
                      </IonButton>
                    )
                  }
                >
                  {t`Badge Printing`}
                </Radio>
              )}
            </RadioGroup>
          </Form>
          <div style={{ flex: 1 }} />
          <Footer>
            <p>{jt`To unlock the device and close Kiosk mode, click the ${lockIcon} icon and log in with your credentials.`}</p>
            <IonButton
              form="kiosk-setup"
              expand="block"
              type="submit"
              role="button"
              disabled={!canLaunchKiosk}
            >
              {t`Launch Kiosk`}
            </IonButton>
          </Footer>
        </Content>
      </IonContent>
      <IonAlert
        isOpen={!!error.header}
        // skipcq: JS-0417
        onDidDismiss={() => setError({ header: '', message: '' })}
        header={error.header}
        message={error.message}
        buttons={[t`OK`]}
      />
    </IonPage>
  );
};
export default Setup;
